import React, { useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import Image from "next/image";
import Link from "next/link";

import {
  FiGrid,
  FiUser,
  FiHome,
  FiShoppingCart,
  FiHeart,
  FiTool,
  FiSettings,
  FiCreditCard,
  FiLogOut,
} from "react-icons/fi";
import { FaHome } from "react-icons/fa";

import { signOut, useSession } from "next-auth/react";
import Button from "@/common/Button";

import { useCartStore } from "@/store/cart";
import apiClient from "@/utils/apiClient";
import { useWishlistStore } from "@/store/wishlist";

const Avatar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const session = useSession();
  const [user, setUser] = useState<any>();
  const { syncCartWithBackend, setInitialCart } = useCartStore();
  const { syncWishlistWithBackend } = useWishlistStore();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const fetchCart = async (userId: any) => {
    if (!user?.id) return;
    try {
      const response = await apiClient.get(
        `${apiClient.URLS.cart}/${user?.id}`,
        {}
      );
      setInitialCart(response.body.items);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  useEffect(() => {
    if (session?.status === "authenticated" && session.data?.user) {
      const sessionUser = session.data?.user;
      setUser(sessionUser);

      syncCartWithBackend(sessionUser.id);
      fetchCart(sessionUser.id);
      syncWishlistWithBackend(sessionUser.id);
    }
  }, [session?.status]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getInitials = (name?: string) => {
    if (!name) return "";
    const names = name.split(" ");
    const initials = names.map((n) => n[0]).join("");
    return initials.toUpperCase();
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await signOut({ callbackUrl: "/" });
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };
  const defaultClasses =
    "flex items-center justify-center rounded-full overflow-hidden bg-gray-400 px-4 py-[10px] cursor-pointer";

  return (
    <div className="relative inline-block text-left px-2" ref={dropdownRef}>
      <div onClick={toggleDropdown} className={twMerge(defaultClasses)}>
        {user?.profile ? (
          <div className="relative w-full h-full">
            <Image
              src={user?.profile}
              alt={user.name || "Avatar"}
              className="object-cover absolute w-full h-full rounded-full"
              fill
            />
          </div>
        ) : (
          <span className="font-Gordita-Bold text-[14px] text-white">
            {getInitials(user?.email)}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="absolute bottom-full md:mb-0 mb-2 md:top-full md:right-0 -right-55 !z-[9999] bg-white max-w-[220px] min-h-[400px]  mt-2  border border-gray-200 rounded-md shadow-lg py-2">
          <div className="flex items-center justify-center px-4">
            <div className="w-13 h-13 mb-2 relative mr-3 flex items-center justify-center px-2">
              {user?.profile ? (
                <Image
                  src={user.profile}
                  alt={user.firstName || "Avatar"}
                  className="object-cover rounded-full"
                  width={50}
                  height={50}
                />
              ) : (
                <div className="flex items-center justify-center p-3 bg-[#5097FF] rounded-full w-[35px] h-9">
                  <span className="font-bold text-white">
                    {getInitials(user?.email)}
                  </span>
                </div>
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-800">{user?.firstName}</p>
              <p className="text-sm text-gray-500">
                {user?.email?.slice(0, 15)}...
              </p>
            </div>
          </div>
          <div className="border-t border-gray-200"></div>
          <div className="py-2 flex flex-col">
            <Link
              href="/user/dashboard"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiGrid className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">DashBoard</span>
            </Link>
            <Link
              href="/user/user-profile"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiUser className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">My Profile</span>
            </Link>
            <Link
              href="/user/properties"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FaHome className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">Properties</span>
            </Link>

            <Link
              href="/cart"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiShoppingCart className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">Cart</span>
            </Link>

            <Link
              href="/user/wishlist"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiHeart className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium  ">
                Wishlist
              </span>
            </Link>
            <Link
              href="/user/custom-builder"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiTool className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">Custom-Builder</span>
            </Link>

            <Link
              href="#"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiSettings className="w-[24px] font-Gordita-Medium" />
              </span>
              <span className="ml-2 font-Gordita-Medium ">
                Settings
              </span>
            </Link>
            <Link
              href="#"
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiCreditCard className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium">Wallet</span>
            </Link>

            <div className="border-t border-gray-200"></div>
            <Button
              onClick={handleLogout}
              className="flex items-center px-4 py-2  bg-white rounded-md text-blue-500 hover:bg-gray-100"
            >
              <span className="material-icons">
                <FiLogOut className="w-[24px] font-Gordita-Medium text-[20px]" />
              </span>
              <span className="ml-2 font-Gordita-Medium font-semibold">
                Logout
              </span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Avatar;
